<template>
  <div class="verification-container">
    <div class="card-grid">
      <div v-for="card in cards" :key="card.id" class="verification-card">
        <div class="card-header">
          <div class="icon-box">
            <img
              v-if="card.id === 1"
              src="~@/assets/icon/user-check.svg"
              alt="icon"
            />
            <img
              v-if="card.id === 2"
              src="~@/assets/icon/doc-check.svg"
              alt="icon"
            />
            <img
              v-if="card.id === 3"
              src="~@/assets/icon/money-paper.svg"
              alt="icon"
            />
            <img
              v-if="card.id === 4"
              src="~@/assets/icon/ticket-check.svg"
              alt="icon"
            />
          </div>

          <StatusBadge
            v-if="card.status"
            :statusClass="card.statusClass"
            :statusText="card.status"
            type="large"
          />
        </div>

        <div class="card-body">
          <h3>{{ card.title }}</h3>
          <p>{{ card.description }}</p>
        </div>

        <div class="card-footer">
          <div v-for="button in card.buttons" :key="button.label">
            <component-guard
              v-if="button.permissions"
              :requiredPermissions="button.permissions"
              operator="OR"
            >
              <button
                :class="['btn', button.variant]"
                @click="button.action"
                :disabled="
                  transferUnitStatus === 'cancelled' ||
                  transferUnitStatus === 'done' ||
                  button.disabled
                "
              >
                {{ button.label }}
              </button>
            </component-guard>
            <button
              v-else
              :class="['btn', button.variant]"
              @click="button.action"
              :disabled="
                transferUnitStatus === 'cancelled' ||
                transferUnitStatus === 'done' ||
                button.disabled
              "
            >
              {{ button.label }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <download-template-doc />
    <upload-application-document />
    <invoice-log-and-status />
    <ticket-log-and-status />
  </div>
</template>

<script>
import DownloadTemplateDoc from "../../CustomComponent/DownloadTemplateDoc.vue";
import UploadApplicationDocument from "../../CustomComponent/UploadApplicationDocument.vue";
import InvoiceLogAndStatus from "../../CustomComponent/InvoiceLogAndStatus.vue";
import TicketLogAndStatus from "../../CustomComponent/TicketLogAndStatus.vue";
import StatusBadge from "../../CustomComponent/StatusBadge.vue";
import { ComponentGuard } from "@/components/Permissions";

import { mapActions, mapGetters } from "vuex";

export default {
  name: "TransferVerification",
  components: {
    DownloadTemplateDoc,
    UploadApplicationDocument,
    InvoiceLogAndStatus,
    TicketLogAndStatus,
    StatusBadge,
    ComponentGuard,
  },

  computed: {
    ...mapGetters("rentAdjustment", {
      transferUnitId: "getId",
      statuses: "getVerificationStatus",
      companyIdTarget: "getCompanyIdTarget",
      transferUnitStatus: "getStatus",
    }),
    lesseeCard() {
      let statusClass = "";
      switch (this.statuses.lesseeStatus) {
        case "Pending":
          statusClass = "black";
          break;
        case "Verified":
          statusClass = "green";
          break;
        case "Rejected":
          statusClass = "red";
          break;
        default:
          statusClass = "yellow";
          break;
      }
      return {
        id: 1,
        title: "Verifikasi Lessee",
        icon: "~@/assets/icon/user-check.svg",
        status: this.statuses.lesseeStatus,
        statusClass,
        description:
          "Verifikasi lessee dilakukan untuk memastikan bahwa semua dokumen perubahan telah lengkap dan legalitasnya telah disetujui.",
        buttons: [
          {
            label: "Detail Lessee",
            variant: "primary",
            action: this.openLessee,
          },
        ],
      };
    },
    documentCard() {
      const statusClassMapping = {
        "belum upload": "black",
        verified: "green",
      };

      const statusClassTextMapping = {
        "belum upload": "Belum Upload",
        verified: "Done",
      };

      return {
        id: 2,
        title: "Verifikasi Dokumen Permohonan",
        icon: "~@/assets/icon/doc-check.svg",
        status: statusClassTextMapping[this.statuses.documentStatus],
        statusClass: statusClassMapping[this.statuses.documentStatus],
        description:
          "Download template surat permohonan jika diperlukan atau upload surat permohonan dan persetujuan yang sudah ditandatangani.",
        buttons: [
          {
            label: "Download Template",
            variant: "outline",
            action: this.downloadTemplate,
            permissions: ["29.1", "30.1"],
          },
          {
            label: "Upload Dokumen",
            variant: "primary",
            action: this.uploadDocument,
            permissions: ["29.2", "30.2"],
          },
        ],
      };
    },
    invoiceCard() {
      const statusClassMapping = {
        warning: "red",
        eligible: "green",
      };
      const statusTextMapping = {
        warning: "Warning",
        eligible: "Eligible",
      };
      return {
        id: 3,
        title: "Invoice",
        icon: "~@/assets/icon/money-paper.svg",
        status: statusTextMapping[this.statuses.invoiceStatus],
        statusClass: statusClassMapping[this.statuses.invoiceStatus],
        description:
          "Pemeriksaan invoice yang berjalan sebelumnya dan tentukan status kelayakannya untuk melanjutkan proses pengalihan unit.",
        buttons: [
          {
            label: "Log & Perbarui Status",
            variant: "primary",
            action: this.openInvoice,
            permissions: ["31.1", "31.2"],
          },
        ],
      };
    },
    ticketCard() {
      const statusClassMapping = {
        warning: "red",
        eligible: "green",
      };
      const statusTextMapping = {
        warning: "Warning",
        eligible: "Eligible",
      };
      return {
        id: 4,
        title: "Tiket Aktif",
        icon: "~@/assets/icon/ticket-check.svg",
        status: statusTextMapping[this.statuses.ticketStatus],
        statusClass: statusClassMapping[this.statuses.ticketStatus],
        description:
          "Pastikan untuk memastikan tidak ada tiket yang sedang berjalan atau aktif saat proses pengalihan unit berlangsung.",
        buttons: [
          {
            label: "Informasi Tiket",
            variant: "primary",
            action: this.openTicket,
            permissions: ["32.1", "32.2"],
          },
        ],
      };
    },
    cards() {
      return [
        this.lesseeCard,
        this.documentCard,
        this.invoiceCard,
        this.ticketCard,
      ];
    },

  },

  methods: {
    ...mapActions("rentAdjustment", [
      "getVerificationLog",
      "getVerificationStatus",
    ]),
    openLessee() {
      this.$router.push(
        `/partnership/lessee/detail/${this.companyIdTarget}?companyStatus=old`
      );
    },

    downloadTemplate() {
      $(`#downloadTemplateModal`).modal("show");
    },

    uploadDocument() {
      $(`#uploadDocumentModal`).modal("show");
    },

    async openInvoice() {
      await this.getVerificationLog({
        transferUnitId: this.transferUnitId,
        type: "invoice",
      });
      $(`#invoiceStatusModal`).modal("show");
    },

    async openTicket() {
      await this.getVerificationLog({
        transferUnitId: this.transferUnitId,
        type: "ticket",
      });
      $(`#ticketStatusModal`).modal("show");
    },
  },

  mounted() {
    const types = ["lessee", "document", "invoice", "ticket"];
    types.forEach((type) => {
      this.getVerificationStatus({ transferUnitId: this.transferUnitId, type });
    });
  },
};
</script>

<style scoped>
.verification-container {
  padding: 24px;
  background-color: #ffffff;
  border: 1px solid #d1d9e2;
  border-radius: 0 0 8px 8px;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.verification-card {
  min-height: 220px;
  padding: 18px;
  border: 1px solid #d9dee7;
  border-radius: 8px;
  background: #fff;

  display: flex;
  flex-direction: column;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background-color: #ffffff !important;
  border: none !important;
}

.icon-box {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f5f7fa;

  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-box i {
  color: #4b5563;
  font-size: 16px;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.warning {
  color: #e67e22;
  background: #fff4e5;
  border: 1px solid #ffd8a8;
}

.secondary {
  color: #6b7280;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
}

.danger {
  color: #dc3545;
  background: #fff0f0;
  border: 1px solid #ffc9c9;
}

.card-body {
  margin-top: 18px;
  flex: 1;
}

.card-body h3 {
  margin: 0 0 12px;
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
}

.card-body p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #6b7280;
}

.card-footer {
  margin-top: 20px;
  background-color: #ffffff !important;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border: none !important;
}

.btn {
  height: 38px;
  padding: 0 18px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.btn.primary {
  border: none;
  background: #1677ff;
  color: white;
}

.btn.outline {
  background: white;
  border: 1px solid #1677ff;
  color: #1677ff;
}

.btn:disabled {
  background-color: #d1d5dc;
  cursor: not-allowed;
  color: #ffffff;
  border: none;
}
</style>


// WEBPACK FOOTER //
// src/components/RentAdjustment/FormTabs/Verification/Verification.vue